import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { alertActions } from "../redux/AlertController";
import useLogout from "./useLogout";

// Groups can hold members of any status, unlike the main applications page
// which defaults to "pending" for reviewing new submissions.
const DEFAULT_FILTERS = { city: '', referenceNumber: 'APP-', phoneNumber: '', status: '' };

export const useGroupMembers = (groupId) => {
    const [group, setGroup] = useState(null);
    const [isLoadingGroup, setIsLoadingGroup] = useState(true);

    const [members, setMembers] = useState([]);
    const [isLoadingMembers, setIsLoadingMembers] = useState(true);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [total, setTotal] = useState(0);
    const [filters, setFilters] = useState(DEFAULT_FILTERS);

    const [isAddingByReference, setIsAddingByReference] = useState(false);
    const [isAddingByPhone, setIsAddingByPhone] = useState(false);
    const [isAddingByFilter, setIsAddingByFilter] = useState(false);
    const [isPreviewingByFilter, setIsPreviewingByFilter] = useState(false);
    const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);

    const dispatch = useDispatch();
    const user = useSelector(state => state.userController.user);
    const navigate = useNavigate();
    const { logout } = useLogout();

    const fetchGroup = async () => {
        setIsLoadingGroup(true);
        try {
            const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/admin/groups/${groupId}`, {
                headers: { 'Authorization': `Bearer ${user?.token}` }
            });
            const json = await res.json();

            if (res.status === 401) {
                logout();
            } else if (!res.ok) {
                dispatch(alertActions.showAlert({ msg: json.message, type: 'error' }));
            } else {
                setGroup(json);
            }
        } catch (err) {
            dispatch(alertActions.showAlert({ msg: err.message, type: 'error' }));
        }
        setIsLoadingGroup(false);
    };

    const fetchMembers = async (page = 1, activeFilters = filters) => {
        setIsLoadingMembers(true);
        try {
            const params = new URLSearchParams({ page, limit: 20 });
            Object.entries(activeFilters).forEach(([key, value]) => {
                if (value !== '' && value !== null && value !== undefined) {
                    params.append(key, value);
                }
            });

            const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/admin/groups/${groupId}/members?${params}`, {
                headers: { 'Authorization': `Bearer ${user?.token}` }
            });
            const json = await res.json();

            if (res.status === 401) {
                logout();
            } else if (!res.ok) {
                dispatch(alertActions.showAlert({ msg: json.message, type: 'error' }));
            } else {
                setMembers(json.applications);
                setCurrentPage(json.currentPage);
                setTotalPages(json.totalPages);
                setTotal(json.total);
            }
        } catch (err) {
            dispatch(alertActions.showAlert({ msg: err.message, type: 'error' }));
        }
        setIsLoadingMembers(false);
    };

    const applyFilters = (newFilters) => {
        setFilters(newFilters);
        fetchMembers(1, newFilters);
    };

    const clearFilters = () => {
        setFilters(DEFAULT_FILTERS);
        fetchMembers(1, DEFAULT_FILTERS);
    };

    // referenceNumbers is a plain array of strings — used both by the manual
    // single/multi entry box (allowMove: false, unchanged) and the Excel
    // bulk-upload, where allowMove: true lets a member already in a
    // different group be reassigned to this one instead of being skipped.
    const addByReferenceNumbers = async (referenceNumbers, allowMove = false) => {
        setIsAddingByReference(true);
        let result = null;
        try {
            const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/admin/groups/${groupId}/members/reference-numbers`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${user?.token}`
                },
                body: JSON.stringify({ referenceNumbers, allowMove })
            });
            const json = await res.json();

            if (res.status === 401) {
                logout();
            } else if (!res.ok) {
                dispatch(alertActions.showAlert({ msg: json.message, type: 'error' }));
            } else {
                result = json;
                const skippedCount = json.skipped?.length || 0;
                const parts = [];
                if (json.addedCount > 0) parts.push(`إضافة ${json.addedCount}`);
                if (json.movedCount > 0) parts.push(`نقل ${json.movedCount} من مجموعات أخرى`);
                if (skippedCount > 0) parts.push(`تخطي ${skippedCount}`);
                dispatch(alertActions.showAlert({
                    msg: parts.length > 0 ? `تمت ${parts.join(' و')}` : 'لم تتم أي إضافة',
                    type: (json.addedCount > 0 || json.movedCount > 0) ? 'success' : 'error'
                }));
                fetchGroup();
                fetchMembers(currentPage, filters);
            }
        } catch (err) {
            dispatch(alertActions.showAlert({ msg: err.message, type: 'error' }));
        }
        setIsAddingByReference(false);
        return result;
    };

    // Same idea as addByReferenceNumbers, for Excel sheets that only have
    // phone numbers. Always allows moving members out of another group,
    // matching the reference-number Excel upload's behavior.
    const addByPhoneNumbers = async (phoneNumbers, allowMove = true) => {
        setIsAddingByPhone(true);
        let result = null;
        try {
            const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/admin/groups/${groupId}/members/phone-numbers`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${user?.token}`
                },
                body: JSON.stringify({ phoneNumbers, allowMove })
            });
            const json = await res.json();

            if (res.status === 401) {
                logout();
            } else if (!res.ok) {
                dispatch(alertActions.showAlert({ msg: json.message, type: 'error' }));
            } else {
                result = json;
                const skippedCount = json.skipped?.length || 0;
                const parts = [];
                if (json.addedCount > 0) parts.push(`إضافة ${json.addedCount}`);
                if (json.movedCount > 0) parts.push(`نقل ${json.movedCount} من مجموعات أخرى`);
                if (skippedCount > 0) parts.push(`تخطي ${skippedCount}`);
                dispatch(alertActions.showAlert({
                    msg: parts.length > 0 ? `تمت ${parts.join(' و')}` : 'لم تتم أي إضافة',
                    type: (json.addedCount > 0 || json.movedCount > 0) ? 'success' : 'error'
                }));
                fetchGroup();
                fetchMembers(currentPage, filters);
            }
        } catch (err) {
            dispatch(alertActions.showAlert({ msg: err.message, type: 'error' }));
        }
        setIsAddingByPhone(false);
        return result;
    };

    // Read-only — returns exactly who addByFilters would add (same filters
    // + limit), so the admin can look before committing.
    const previewByFilters = async (filterCriteria) => {
        setIsPreviewingByFilter(true);
        let preview = null;
        try {
            const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/admin/groups/${groupId}/members/filters/preview`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${user?.token}`
                },
                body: JSON.stringify(filterCriteria)
            });
            const json = await res.json();

            if (res.status === 401) {
                logout();
            } else if (!res.ok) {
                dispatch(alertActions.showAlert({ msg: json.message, type: 'error' }));
            } else {
                preview = json;
            }
        } catch (err) {
            dispatch(alertActions.showAlert({ msg: err.message, type: 'error' }));
        }
        setIsPreviewingByFilter(false);
        return preview;
    };

    const addByFilters = async (filterCriteria) => {
        setIsAddingByFilter(true);
        let success = false;
        try {
            const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/admin/groups/${groupId}/members/filters`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${user?.token}`
                },
                body: JSON.stringify(filterCriteria)
            });
            const json = await res.json();

            if (res.status === 401) {
                logout();
            } else if (!res.ok) {
                dispatch(alertActions.showAlert({ msg: json.message, type: 'error' }));
            } else {
                dispatch(alertActions.showAlert({ msg: `تمت إضافة ${json.addedCount} طلب للمجموعة`, type: 'success' }));
                success = true;
                fetchGroup();
                fetchMembers(currentPage, filters);
            }
        } catch (err) {
            dispatch(alertActions.showAlert({ msg: err.message, type: 'error' }));
        }
        setIsAddingByFilter(false);
        return success;
    };

    // Same endpoint the main applications page uses — a member keeps its
    // group regardless of status changes.
    const updateMemberStatus = async (id, { status, rejectionReason }) => {
        setIsUpdatingStatus(true);
        let success = false;
        try {
            const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/admin/applications/${id}/status`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${user?.token}`
                },
                body: JSON.stringify({ status, rejectionReason })
            });
            const json = await res.json();

            if (res.status === 401) {
                logout();
            } else if (!res.ok) {
                dispatch(alertActions.showAlert({ msg: json.message, type: 'error' }));
            } else {
                dispatch(alertActions.showAlert({ msg: 'تم تحديث حالة الطلب بنجاح', type: 'success' }));
                success = true;
                fetchMembers(currentPage, filters);
            }
        } catch (err) {
            dispatch(alertActions.showAlert({ msg: err.message, type: 'error' }));
        }
        setIsUpdatingStatus(false);
        return success;
    };

    useEffect(() => {
        if (!user) {
            navigate('/login');
        } else if (groupId) {
            fetchGroup();
            fetchMembers(1, DEFAULT_FILTERS);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user, groupId]);

    return {
        group,
        isLoadingGroup,
        fetchGroup,
        members,
        isLoadingMembers,
        currentPage,
        totalPages,
        total,
        filters,
        fetchMembers,
        applyFilters,
        clearFilters,
        addByReferenceNumbers,
        isAddingByReference,
        addByPhoneNumbers,
        isAddingByPhone,
        previewByFilters,
        isPreviewingByFilter,
        addByFilters,
        isAddingByFilter,
        updateMemberStatus,
        isUpdatingStatus
    };
};
